import { useRef, useState, useMemo, useEffect } from 'react';
import Webcam from 'react-webcam';
import { useGeolocated } from 'react-geolocated';
import { Box, Stack, Paper, Chip, Button, TextField, CircularProgress, Typography, IconButton } from '@mui/material';
import { CameraAlt, AddAPhoto, GpsFixed, DriveFileRenameOutline, CheckCircle, FlashlightOn, FlashlightOff } from '@mui/icons-material';
import { MIMEURLToByte, postToDB, postNewCap, postTitle } from '../Common';
import { parseCartellone, votoColor, votoLabel, IGNOTA } from '../lib/parse';
import RecorderInline from './RecorderInline';

// risoluzione: si chiede il massimo che la camera offre (ideal = soft constraint,
// il browser concede il meglio disponibile; i vecchi 1280×1024 buttavano via ~10x di pixel)
const VCONST = { width: { ideal: 4096 }, height: { ideal: 3072 }, facingMode: 'environment' };

const CSS_MAX_ZOOM = 5;   // limite dello zoom digitale (oltre, il ritaglio degrada troppo)

// Sul computer (puntatore preciso) la posizione non viene da un GPS ma da Windows, che la
// ricava dal Wi-Fi: un PC fisso via cavo spesso non la trova anche con tutto consentito.
const COMPUTER = typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: fine)').matches;
const WINDOWS_POSIZIONE = 'nelle impostazioni di Windows (Privacy e sicurezza → Posizione)';
const SENZA_WIFI = 'Senza Wi-Fi o GPS il computer spesso non la trova: per censire usa il telefono.';

export default function ScanView({ notify, onDataChanged, vedeVoto = true }) {
  const webcamref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [elemid, setElemid] = useState(null);
  const [title, setTitle] = useState('');
  const [titoloSalvato, setTitoloSalvato] = useState(null);   // titolo dell'ultimo cartellone salvato
  const [nCntx, setNCntx] = useState(0);

  /* --- zoom fotocamera: nativo se la camera lo supporta, altrimenti digitale --- */
  const [zoom, setZoomState] = useState(1);
  const nativeZoomRef = useRef(null);            // { track, min, max }
  const trackRef = useRef(null);
  const [torch, setTorch] = useState(null);      // null = non disponibile, false/true = stato
  const [camRes, setCamRes] = useState('');
  const [camErr, setCamErr] = useState(null);    // nome dell'errore della fotocamera (permesso negato, nessuna camera…)
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);

  const applyZoom = (z) => {
    const nz = nativeZoomRef.current;
    const lim = nz ? Math.min(8, nz.max) : CSS_MAX_ZOOM;
    const clamped = Math.min(lim, Math.max(1, z));
    setZoomState(clamped);
    if (nz) nz.track.applyConstraints({ advanced: [{ zoom: Math.max(nz.min, clamped) }] }).catch(() => {});
  };

  const onUserMedia = (stream) => {
    setCamErr(null);
    try {
      const track = stream.getVideoTracks()[0];
      trackRef.current = track;
      const caps = track.getCapabilities?.() || {};
      if (caps.zoom && caps.zoom.max > (caps.zoom.min ?? 1)) {
        nativeZoomRef.current = { track, min: caps.zoom.min ?? 1, max: caps.zoom.max };
      }
      if (caps.torch) setTorch(false);
      const s = track.getSettings?.() || {};
      if (s.width) setCamRes(`${s.width}×${s.height}`);
    } catch { /* nessuno zoom nativo: si usa quello digitale */ }
  };

  const toggleTorch = () => {
    const track = trackRef.current;
    if (!track || torch == null) return;
    track.applyConstraints({ advanced: [{ torch: !torch }] })
      .then(() => setTorch(!torch))
      .catch(() => notify('Torcia non disponibile su questa camera', 'error'));
  };

  const camPointerDown = (e) => {
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = { d: Math.hypot(a.x - b.x, a.y - b.y), z: zoom };
    }
  };
  const camPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2 && pinchRef.current) {
      const [a, b] = [...pointersRef.current.values()];
      applyZoom(pinchRef.current.z * (Math.hypot(a.x - b.x, a.y - b.y) / pinchRef.current.d));
    }
  };
  const camPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
  };

  /** Scatta alla risoluzione piena del flusso video, rispettando lo zoom:
   *  nativo = frame già zoomato dall'hardware; digitale = ritaglio centrale. */
  const captureFrame = () => {
    const video = webcamref.current?.video;
    if (!video || !video.videoWidth) return null;
    const vw = video.videoWidth, vh = video.videoHeight;
    const digital = !nativeZoomRef.current && zoom > 1.01;
    const sw = digital ? vw / zoom : vw, sh = digital ? vh / zoom : vh;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw); canvas.height = Math.round(sh);
    canvas.getContext('2d').drawImage(video, (vw - sw) / 2, (vh - sh) / 2, sw, sh, 0, 0, canvas.width, canvas.height);
    let out = canvas.toDataURL('image/jpeg', 0.87);
    if (out.length > 8_000_000) out = canvas.toDataURL('image/jpeg', 0.75);   // tetto ~6MB reali per l'upload
    return out;
  };

  // Nessun limite di attesa: con il vecchio limite di 15 secondi, sul computer (dove la
  // posizione arriva lenta, o non arriva) l'app dichiarava il GPS spento e toglieva la
  // fotocamera, senza modo di riprovare. Ora si aspetta, e dopo 15 secondi si spiega dove
  // attivarla; con un errore vero (permesso negato, posizione non disponibile) c'è «Riprova».
  const { coords, isGeolocationAvailable, isGeolocationEnabled, positionError, getPosition } = useGeolocated({
    positionOptions: { enableHighAccuracy: true, maximumAge: 0, timeout: Infinity },
    watchPosition: true,
  });
  const gpsOff = !isGeolocationAvailable || !isGeolocationEnabled;
  const [attesaLunga, setAttesaLunga] = useState(false);
  useEffect(() => {
    if (coords) { setAttesaLunga(false); return; }
    const t = setTimeout(() => setAttesaLunga(true), 15000);
    return () => clearTimeout(t);
  }, [coords]);
  const motivoNoGps = !isGeolocationAvailable
    ? 'Questo browser non fornisce la posizione: per censire usa il telefono.'
    : positionError?.code === 1
      ? `La posizione è bloccata per questo sito: consentila ${COMPUTER ? "dall'icona a sinistra dell'indirizzo" : 'nelle impostazioni del browser'}, poi premi Riprova.`
      : COMPUTER
        ? `Il computer non fornisce la posizione: controlla che sia attiva ${WINDOWS_POSIZIONE} e premi Riprova. ${SENZA_WIFI}`
        : 'Posizione non disponibile: attiva la localizzazione del telefono, poi premi Riprova.';

  // coordsRef sempre aggiornato: il setTimeout di shotPrimary deve leggere il fix GPS fresco, non la closure
  const coordsRef = useRef(coords);
  useEffect(() => { coordsRef.current = coords; }, [coords]);

  const meta = useMemo(() => parseCartellone({ title, dataChange: null }), [title]);
  const vc = votoColor(meta.voto);

  const shotPrimary = () => {
    const img = captureFrame();
    if (!img) { notify('Fotocamera non pronta', 'error'); return; }
    // Il titolo scritto prima dello scatto parte INSIEME alla foto. In origine andava
    // assegnato con un secondo pulsante attivo solo dopo lo scatto: chi non lo notava
    // si ritrovava il cartellone salvato come "id 887" e il titolo da rimettere a mano.
    // Si legge adesso, non dopo i 4 secondi: conta ciò che c'era quando si è premuto.
    const tit = title.trim() || null;
    setBusy(true);
    // La posizione la tiene aggiornata il tracciamento aperto all'avvio: qui si aspetta solo
    // che si assesti. Prima si chiamava getPosition(), che apriva un tracciamento in più a
    // ogni scatto senza chiudere i precedenti (a fine giro decine di GPS attivi insieme).
    setTimeout(() => {   // come nell'app originale: 4s per assestare il GPS
      // Il voto NON si invia: si ricava dal titolo ("V8"). Salvarlo a parte lo
      // renderebbe incoerente con una rinomina successiva, che aggiorna solo il titolo.
      const c = { id: null, latitude: coordsRef.current?.latitude, longitude: coordsRef.current?.longitude,
                  objimgsrc: MIMEURLToByte(img), creationdate: null, title: tit };
      postToDB(c, setBusy, () => notify('Errore: il cartellone NON è stato salvato, riprova', 'error'), (ret) => {
        setElemid(ret.id);
        setNCntx(0);
        setTitoloSalvato(tit);
        // Campo svuotato per il cartellone successivo. Se restasse il titolo appena
        // usato, uno scatto distratto al cartellone dopo lo duplicherebbe: e un titolo
        // sbagliato è peggio di uno mancante, perché sembra giusto.
        setTitle('');
        if (tit) notify(`Salvato ✓ «${tit}» — cartellone ${ret.id}`, 'success');
        else notify(`Salvato ✓ cartellone ${ret.id}, ma senza titolo: scrivilo e premi «Rinomina»`, 'warning');
        onDataChanged();
      });
    }, 4000);
  };

  const shotSecondary = () => {
    const img = captureFrame();
    if (!img || elemid == null) return;
    setBusy(true);
    setTimeout(() => {
      postNewCap(MIMEURLToByte(img), elemid, setBusy, () => {
        setNCntx(n => n + 1);
        notify(`Foto contesto aggiunta al ${elemid} ✓`, 'success');
        onDataChanged();
      }, () => notify('Errore: foto contesto non salvata', 'error'));
    }, 1000);
  };

  /** Corregge il titolo dell'ultimo cartellone salvato (quello su cui lavorano anche contesto e nota vocale). */
  const rinomina = () => {
    const tit = title.trim();
    if (elemid == null || !tit) return;
    postTitle({ id: elemid, title: tit }, (r) => {
      if (r === 'success') {
        setTitoloSalvato(tit);
        setTitle('');
        notify(`Titolo del cartellone ${elemid} aggiornato ✓`, 'success');
        onDataChanged();
      } else {
        notify('Errore: titolo non salvato', 'error');
      }
    }, null);
  };

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Box onPointerDown={camPointerDown} onPointerMove={camPointerMove}
           onPointerUp={camPointerUp} onPointerCancel={camPointerUp}
           onDoubleClick={() => applyZoom(1)}
           sx={{ position: 'relative', flex: 1, minHeight: 200, bgcolor: '#0f172a', overflow: 'hidden',
                 touchAction: 'none' }}>
        {!gpsOff && (
          <Webcam audio={false} ref={webcamref} videoConstraints={VCONST}
                  onUserMedia={onUserMedia} onUserMediaError={(e) => setCamErr(e?.name || 'Error')}
                  style={{ width: '100%', height: '100%', objectFit: 'cover',
                           transform: nativeZoomRef.current ? 'none' : `scale(${zoom})`,
                           transformOrigin: 'center center' }} />
        )}
        {/* Zoom e indicazioni stanno su due righe, entrambe centrate: affiancati, su un
            telefono l'etichetta lunga finiva sopra i pulsanti 2× e 4×. Tutto è rialzato
            di 20 px perché il pannello comandi sotto copre il fondo dell'inquadratura. */}
        <Stack direction="row" spacing={0.8}
               onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
               sx={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)' }}>
          {[1, 2, 4].map(z => (
            <Chip key={z} label={`${z}×`} onClick={() => applyZoom(z)}
                  sx={{ fontWeight: 800, fontSize: 14, px: 0.5, cursor: 'pointer',
                        bgcolor: Math.abs(zoom - z) < 0.05 ? '#1d4ed8' : 'rgba(15,23,42,.7)',
                        color: 'white', border: '1px solid rgba(255,255,255,.35)',
                        '&:hover': { bgcolor: '#1d4ed8' } }} />
          ))}
        </Stack>
        {torch != null && (
          <IconButton onClick={toggleTorch}
                      onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
                      sx={{ position: 'absolute', top: 6, right: 6, color: torch ? '#fbbf24' : 'white',
                            bgcolor: 'rgba(15,23,42,.7)', '&:hover': { bgcolor: 'rgba(15,23,42,.9)' } }}>
            {torch ? <FlashlightOn /> : <FlashlightOff />}
          </IconButton>
        )}
        <Chip size="small"
              label={`${zoom > 1.01 ? `${zoom.toFixed(1)}× — doppio tap per tornare a 1×` : 'pizzica per zoomare'}${camRes ? ` · ${camRes}` : ''}`}
              sx={{ position: 'absolute', bottom: 64, left: '50%', transform: 'translateX(-50%)',
                    maxWidth: 'calc(100% - 20px)', bgcolor: 'rgba(15,23,42,.7)', color: 'white', fontSize: 11 }} />
        {gpsOff && (
          <Stack alignItems="center" justifyContent="center" spacing={1.5}
                 sx={{ height: '100%', color: 'white', p: 3, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 700 }}>Per censire i cartelloni serve la posizione</Typography>
            <Typography sx={{ color: '#cbd5e1', maxWidth: 560 }}>{motivoNoGps}</Typography>
            {isGeolocationAvailable && <Button variant="contained" onClick={getPosition}>Riprova</Button>}
          </Stack>
        )}
        {!gpsOff && camErr && (
          <Stack alignItems="center" justifyContent="center" spacing={1.5}
                 sx={{ position: 'absolute', inset: 0, color: 'white', p: 3, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 700 }}>Fotocamera non disponibile</Typography>
            <Typography sx={{ color: '#cbd5e1', maxWidth: 560 }}>
              {camErr === 'NotFoundError' || camErr === 'OverconstrainedError'
                ? 'Nessuna fotocamera trovata su questo dispositivo.'
                : `Consenti la fotocamera per questo sito ${COMPUTER ? "dall'icona a sinistra dell'indirizzo" : 'nelle impostazioni del browser'}, poi ricarica la pagina.`}
            </Typography>
          </Stack>
        )}
        <Chip icon={<GpsFixed sx={{ color: `${gpsOff ? '#f87171' : '#22c55e'} !important` }} />}
              label={coords ? `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}` : gpsOff ? 'GPS non disponibile' : 'in attesa del GPS…'}
              sx={{ position: 'absolute', top: 10, left: 10, bgcolor: 'rgba(15,23,42,.8)', color: 'white', fontWeight: 600 }} />
        {busy && (
          <Stack alignItems="center" justifyContent="center"
                 sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(15,23,42,.55)' }}>
            <CircularProgress sx={{ color: 'white' }} />
            <Typography sx={{ color: 'white', mt: 1 }}>acquisizione…</Typography>
          </Stack>
        )}
      </Box>

      <Paper sx={{ borderRadius: '14px 14px 0 0', p: 2, pb: 2.5, mt: -1.5, zIndex: 2 }}>
        <Stack spacing={1.2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {/* concessionaria e voto dal titolo sono convenzioni nostre: a un concessionario non servono */}
            {vedeVoto && (<>
              <Chip size="small" variant="outlined" label={meta.concessionaria === IGNOTA ? 'Concessionaria —' : meta.concessionaria}
                    sx={{ color: meta.concessionaria === IGNOTA ? '#94a3b8' : '#1d4ed8', fontWeight: 700 }} />
              <Chip size="small" label={`Voto ${votoLabel(meta.voto)}`}
                    sx={{ bgcolor: vc.bg, color: vc.fg, fontWeight: 800 }} />
            </>)}
            {elemid != null
              ? <Chip size="small" icon={<CheckCircle />} label={`#${elemid} salvato`} sx={{ bgcolor: '#ecfdf5', color: '#15803d', '& .MuiChip-icon': { color: 'inherit' } }} />
              : <Chip size="small" icon={<CheckCircle />} label="Pronto a scattare" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', '& .MuiChip-icon': { color: 'inherit' } }} />}
          </Stack>

          <Stack direction="row" spacing={1}>
            <TextField size="small" fullWidth label="Titolo cartellone"
                       placeholder={vedeVoto ? 'es. DAMIR NO CIMASA FRONTE ELENKA V8' : 'es. Viale Strasburgo, fronte farmacia'}
                       helperText={elemid == null ? 'Scrivilo prima dello scatto: viene salvato insieme alla foto' : ' '}
                       value={title} onChange={(e) => setTitle(e.target.value)} sx={{ minWidth: 0 }} />
            {/* Solo dopo uno scatto, e con il numero: deve essere chiaro QUALE cartellone
                si rinomina, per non confonderlo col titolo del prossimo.
                flexShrink 0: senza, su telefono il pulsante si stringeva sotto la larghezza
                del suo testo (che non va a capo) e l'etichetta debordava dal bordo. */}
            {elemid != null && (
              <Button variant="outlined" startIcon={<DriveFileRenameOutline />} disabled={busy || !title.trim()}
                      onClick={rinomina}
                      sx={{ whiteSpace: 'nowrap', alignSelf: 'flex-start', height: 40, flexShrink: 0 }}>
                Rinomina #{elemid}
              </Button>
            )}
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="large" fullWidth startIcon={<CameraAlt />}
                    disabled={busy || !coords} onClick={shotPrimary}>
              Scatta principale
            </Button>
            <Button variant="outlined" size="large" fullWidth startIcon={<AddAPhoto />}
                    disabled={busy || elemid == null} onClick={shotSecondary}>
              Scatta contesto
            </Button>
          </Stack>

          {/* Il pulsante grigio senza spiegazione sembrava un guasto: l'unico indizio era
              un'etichetta in alto sull'immagine, facile da non vedere. */}
          {!coords && !busy && !gpsOff && (
            <Typography variant="caption" sx={{ color: '#c2410c', textAlign: 'center', fontWeight: 600 }}>
              📍 In attesa della posizione GPS — «Scatta principale» si attiva appena arriva
              {attesaLunga && (<><br />{COMPUTER
                ? `Se non arriva, controlla che sia attiva ${WINDOWS_POSIZIONE} e ricarica la pagina. ${SENZA_WIFI}`
                : 'Se non arriva, controlla che la localizzazione del telefono sia attiva e che il browser abbia il permesso.'}</>)}
            </Typography>
          )}

          <RecorderInline elem={elemid} notify={notify} />

          {elemid != null && (
            <Typography variant="caption" color="text.secondary">
              Ultimo salvato: <b>#{elemid}</b> {titoloSalvato ? <>«{titoloSalvato}»</> : <i>(senza titolo)</i>}
              {' · '}{nCntx} foto contesto. Foto di contesto e nota vocale si aggiungono a questo cartellone.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
